import React, { useEffect, useMemo, useState } from 'react';
import { CelebrationData } from '../types';

interface CelebrationOverlayProps {
  celebration: CelebrationData;
  onDismiss: () => void;
}

type CelebrationTone = 'success' | 'warning' | 'danger' | 'neutral';

interface CelebrationContent {
  title: string;
  subtitle?: string;
  description?: string;
  tone: CelebrationTone;
  badge?: string;
  primaryChips?: string[];
  secondaryChips?: string[];
  hint?: string;
  letter?: string;
}

const AUTO_DISMISS_MS = 2500;

const toneStyles: Record<CelebrationTone, { gradient: string; accent: string }> = {
  success: { gradient: 'linear-gradient(135deg, #16a34a 0%, #22c55e 55%, #10b981 100%)', accent: '#16a34a' },
  warning: { gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 55%, #facc15 100%)', accent: '#f59e0b' },
  danger: { gradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 55%, #f97316 100%)', accent: '#dc2626' },
  neutral: { gradient: 'linear-gradient(135deg, #1f2937 0%, #334155 55%, #475569 100%)', accent: '#94a3b8' }
};

const buildCelebrationContent = (celebration: CelebrationData, isMaster: boolean): CelebrationContent => {
  switch (celebration.type) {
    case 'playersGuessedSame':
      return {
        title: isMaster ? 'Contact Successful' : 'Perfect Match!',
        subtitle: isMaster ? 'Players matched on word' : 'You all guessed',
        tone: isMaster ? 'warning' : 'success',
        badge: isMaster ? '!' : '✓',
        primaryChips: celebration.word ? [celebration.word] : undefined,
        hint: celebration.hintText,
        letter: celebration.newLetter
      };
    case 'playersGuessedDifferent':
      return {
        title: isMaster ? 'No Match!' : 'Mismatch!',
        subtitle: 'Players guessed',
        tone: isMaster ? 'success' : 'warning',
        badge: isMaster ? '🛡️' : '?',
        primaryChips: celebration.words?.filter(word => word.trim())
      };
    case 'masterGuessedCorrect':
      return {
        title: isMaster ? 'Contact Broken!' : 'Master Blocked!',
        subtitle: 'Master guessed',
        tone: isMaster ? 'success' : 'danger',
        badge: isMaster ? '🛡️' : '✕',
        hint: celebration.hintText,
        primaryChips: celebration.word ? [celebration.word] : undefined
      };
    case 'masterGuessedWrong':
      return {
        title: isMaster ? 'Wrong Guess!' : 'Master Missed!',
        subtitle: isMaster ? 'You guessed' : 'Master guessed',
        tone: isMaster ? 'danger' : 'success',
        badge: isMaster ? '!' : '★',
        primaryChips: celebration.masterGuess ? [celebration.masterGuess] : undefined,
        secondaryChips: celebration.playerWord ? [celebration.playerWord] : undefined,
        letter: celebration.newLetter
      };
    case 'gameOverMasterWins':
      return {
        title: isMaster ? 'Victory!' : 'Game Over',
        subtitle: isMaster ? 'You defended the word' : 'The word was',
        tone: isMaster ? 'success' : 'neutral',
        badge: isMaster ? '👑' : '⚑',
        primaryChips: celebration.word ? [celebration.word] : undefined
      };
    case 'gameOverPlayersWin':
      return {
        title: isMaster ? 'Game Over' : 'You Win!',
        subtitle: isMaster ? 'Players guessed' : 'The word was',
        tone: isMaster ? 'danger' : 'success',
        badge: isMaster ? '⚑' : '★',
        primaryChips: celebration.secretWord ? [celebration.secretWord] : undefined,
        secondaryChips: celebration.word ? [celebration.word] : undefined
      };
    case 'hintTimeoutNoChoice':
      return {
        title: isMaster ? 'Defended!' : "Time's Up!",
        subtitle: isMaster ? 'TIME EXPIRED' : 'NO HINT SELECTED',
        description: isMaster 
          ? 'No hint was chosen.\nYour secret is safe!' 
          : 'Vote together faster\nto choose a hint!',
        tone: isMaster ? 'success' : 'danger',
        badge: isMaster ? '🛡️ Word Protected' : '⏱️ Timeout'
      };
    case 'playersGuessedDifferentContactBroken':
      return {
        title: isMaster ? 'Safe!' : 'Double Fail!',
        subtitle: isMaster ? "Players didn't match" : 'Players guessed differently',
        tone: isMaster ? 'success' : 'danger',
        badge: isMaster ? '🛡️' : '✕',
        primaryChips: celebration.words?.filter(word => word.trim()),
        secondaryChips: celebration.masterGuess ? [celebration.masterGuess] : undefined
      };
    case 'playerGuessedWholeWord':
      return {
        title: isMaster ? 'Word Guessed!' : 'Winner!',
        subtitle: celebration.guesser ? `${celebration.guesser} guessed` : 'A player guessed',
        tone: isMaster ? 'danger' : 'success',
        badge: isMaster ? '!' : '★',
        primaryChips: celebration.word ? [celebration.word] : undefined
      };
    case 'contact':
      return {
        title: 'Contact Made!',
        subtitle: 'The chain continues',
        tone: 'success',
        badge: '🎯'
      };
    case 'guessCorrect':
      return {
        title: isMaster ? 'Word Guessed!' : 'Correct Guess!',
        subtitle: isMaster ? 'Your secret was found' : 'Nice work',
        tone: isMaster ? 'danger' : 'success',
        badge: '🎉'
      };
    case 'masterDefense':
      return {
        title: 'Master Defended!',
        subtitle: 'Contact was blocked',
        tone: 'success',
        badge: '🛡️'
      };
    default:
      return {
        title: 'Success!',
        tone: 'neutral'
      };
  }
};

const shouldConfetti = (type: CelebrationData['type'], isMaster: boolean) => {
  switch (type) {
    case 'playersGuessedSame':
      return !isMaster;
    case 'masterGuessedCorrect':
      return isMaster;
    case 'masterGuessedWrong':
      return !isMaster;
    case 'gameOverMasterWins':
      return isMaster;
    case 'gameOverPlayersWin':
      return !isMaster;
    case 'hintTimeoutNoChoice':
      return isMaster;
    case 'playersGuessedDifferentContactBroken':
      return isMaster;
    case 'playerGuessedWholeWord':
      return !isMaster;
    default:
      return false;
  }
};

export const CelebrationOverlay: React.FC<CelebrationOverlayProps> = ({
  celebration,
  onDismiss
}) => {
  // Use the isMaster value captured at celebration time, not the current state
  const celebrationIsMaster = celebration.isMaster ?? false;
  const [confetti, setConfetti] = useState<Array<{ id: number; left: number; delay: number }>>([]);

  const content = useMemo(() => buildCelebrationContent(celebration, celebrationIsMaster), [celebration, celebrationIsMaster]);
  const toneStyle = toneStyles[content.tone];

  useEffect(() => {
    const dismissTimer = setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(dismissTimer);
  }, [onDismiss]);

  useEffect(() => {
    if (!shouldConfetti(celebration.type, celebrationIsMaster)) {
      setConfetti([]);
      return;
    }

    const timer = setTimeout(() => {
      const pieces = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.5
      }));
      setConfetti(pieces);
    }, 300);

    return () => clearTimeout(timer);
  }, [celebration.type, celebrationIsMaster]);

  return (
    <div
      onClick={onDismiss}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(10, 10, 15, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        cursor: 'pointer'
      }}
    >
      <div
        className="celebration-card"
        style={{
          background: toneStyle.gradient,
          borderColor: toneStyle.accent
        }}
      >
        {content.badge && (
          <div className="celebration-badge">
            {content.badge}
          </div>
        )}
        <h2 className="celebration-title">{content.title}</h2>
        {content.subtitle && <p className="celebration-subtitle">{content.subtitle}</p>}
        {content.description && (
          <p style={{
            color: 'white',
            fontSize: '16px',
            fontWeight: '500',
            margin: '12px 0 0 0',
            lineHeight: '1.6',
            textAlign: 'center',
            opacity: 0.95
          }}>
            {content.description}
          </p>
        )}

        {content.primaryChips && content.primaryChips.length > 0 && (
          <div className="celebration-chip-row">
            {content.primaryChips.map((chip, index) => (
              <span key={`${chip}-${index}`} className="celebration-chip">
                {chip.toUpperCase()}
              </span>
            ))}
          </div>
        )}

        {content.secondaryChips && content.secondaryChips.length > 0 && (
          <div className="celebration-chip-row">
            {content.secondaryChips.map((chip, index) => (
              <span key={`${chip}-${index}`} className="celebration-chip secondary">
                {chip.toUpperCase()}
              </span>
            ))}
          </div>
        )}

        {content.hint && (
          <div className="celebration-hint">
            Hint used: {content.hint}
          </div>
        )}

        {content.letter && (
          <div className="celebration-letter">
            {content.letter.toUpperCase()}
          </div>
        )}
      </div>

      {confetti.map(piece => (
        <div
          key={piece.id}
          style={{
            position: 'absolute',
            top: '-10px',
            left: `${piece.left}%`,
            width: '10px',
            height: '10px',
            background: `hsl(${Math.random() * 360}, 70%, 60%)`,
            animation: `confetti 3s ease-out ${piece.delay}s forwards`,
            borderRadius: '2px'
          }}
        />
      ))}
    </div>
  );
};
